import { useMemo } from 'react'
import { task, type Task, type TaskOptions } from 'mobx-task'
import { values } from 'lodash-es'

/**
 * Turns the callback into a task which can be pattern-matched on.
 */
export function useTask<A extends unknown[], R>(
  func: (...args: A) => R,
  options?: TaskOptions<A, R>,
  deps?: unknown[],
): Task<A, R> {
  // eslint-disable-next-line react-hooks/use-memo, react-hooks/exhaustive-deps
  const optsMemo = useMemo(() => options, values(options))
  // eslint-disable-next-line react-hooks/use-memo, react-hooks/exhaustive-deps
  return useMemo(() => task(func, options), [optsMemo, ...(deps ?? [])])
}

/**
 * Overload for {@link useTask} which configures the task to start out in the
 * resolved state.
 */
useTask.resolved = <A extends unknown[], R>(
  func: (...args: A) => R,
  deps?: unknown[],
): Task<A, R> =>
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useTask(func, { state: 'resolved' }, deps)

/**
 * Overload for {@link useTask} which configures the task to start out in the
 * pending state.
 */
useTask.pending = <A extends unknown[], R>(
  func: (...args: A) => R,
  deps?: unknown[],
): Task<A, R> =>
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useTask(func, { state: 'pending' }, deps)
